import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Camera, Calculator, FileText, Layers, Upload, UserRound, WandSparkles } from 'lucide-react';
import ToolStart, { type StudyTool } from './components/ToolStart';
import { useGuestDemo } from '@/contexts/GuestDemoContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import UploadSheet from '@/components/feature/UploadSheet';
import LoadingOverlay from '@/components/feature/LoadingOverlay';
import DemoGate from '@/components/feature/DemoGate';
import AccountRequiredGate from '@/components/feature/AccountRequiredGate';
import StudyMenu from './components/StudyMenu';
import HomeComposer from './HomeComposer';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import './home.css';

const tools = [
  { id: 'Quizify', title: 'Practice', description: 'Practice one question at a time', icon: WandSparkles },
  { id: 'Flashcards', title: 'Study Cards', description: 'Remember the important ideas', icon: Layers },
  { id: 'Calculator', title: 'Smart Calculator', description: 'Expressions, steps & graphs', icon: Calculator },
  { id: 'TLDR', title: 'Quick Learn', description: 'Less reading. More understanding.', icon: FileText },
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { canScan, demoState, useScan: consumeScan, isDevMode } = useGuestDemo();
  const { user, isRehydrated } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [composerPrompt, setComposerPrompt] = useState('');
  const [imageData, setImageData] = useState<string | null>(null);
  const [accountGateOpen, setAccountGateOpen] = useState(false);
  const attachmentReadRef = useRef(0);
  const [activeTool, setActiveTool] = useState<StudyTool | null>(null);
  const [showStudyMenu, setShowStudyMenu] = useState(false);
  const [showUploadSheet, setShowUploadSheet] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [fileError, setFileError] = useState('');
  const [showReturningGate, setShowReturningGate] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const composerCameraRef = useRef<HTMLInputElement>(null);
  const composerImageRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (location.pathname !== '/') return;
    const requestedTool = new URLSearchParams(location.search).get('tool');
    // Keep existing saved links working while accepting the restored public names.
    const tool = tools.find((item) => item.id === requestedTool || item.title === requestedTool);
    if (tool) setActiveTool(tool.id as StudyTool);
  }, [location.pathname, location.search]);

  const closeTool = () => {
    setActiveTool(null);
    if (location.search.includes('tool=')) navigate('/', { replace: true });
  };

  useEffect(() => {
    if (isDevMode || !demoState.demoCompleted || demoState.scansUsed === 0 || location.pathname !== '/') return;
    if (sessionStorage.getItem('lavyn_returning_gate_shown')) return;
    const timer = setTimeout(() => {
      setShowReturningGate(true);
      sessionStorage.setItem('lavyn_returning_gate_shown', '1');
    }, 1500);
    return () => clearTimeout(timer);
  }, [demoState.demoCompleted, demoState.scansUsed, isDevMode, location.pathname]);

  const handleScanClick = () => {
    if (!canScan) { navigate('/scan/start'); return; }
    setFileError('');
    scanInputRef.current?.click();
  };

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>, typeLabel?: string) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!consumeScan()) { navigate('/scan/start'); return; }
    setFileError('');
    if (typeLabel) setUploadStatus(typeLabel);
    const reader = new FileReader();
    reader.onerror = () => {
      setUploadStatus('');
      setFileError('That file could not be read. Please choose it again.');
    };
    reader.onload = () => {
      try {
        // A fresh legacy scan must not inherit a prior Home composer's mode.
        const context = JSON.parse(sessionStorage.getItem('scan_context') || '{}');
        if (context.fromHomeComposer) {
          delete context.fromHomeComposer;
          delete context.homeLearningMode;
          sessionStorage.setItem('scan_context', JSON.stringify(context));
        }
        sessionStorage.setItem('scan_input_image', reader.result as string);
        if (typeLabel) sessionStorage.setItem('scan_input_type', 'document');
        else sessionStorage.removeItem('scan_input_type');
        setUploadStatus('');
        navigate('/scan/output');
      } catch {
        setUploadStatus('');
        setFileError('This file is too large to open. Try a smaller image or PDF.');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUploadSelect = (type: 'pdf' | 'image' | 'camera') => {
    setShowUploadSheet(false);
    if (!canScan) { navigate('/scan/start'); return; }
    const input = type === 'camera' ? cameraInputRef : type === 'image' ? imageInputRef : pdfInputRef;
    input.current?.click();
  };

  const startGeneration = (prompt: string, image: string | null) => {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt && !image) return;
    if (!isRehydrated) return;
    if (!user) {
      setAccountGateOpen(true);
      return;
    }
    if (cleanPrompt.length > 12000) {
      setFileError('Please keep your question under 12,000 characters, or attach a photo of the full question.');
      return;
    }
    try {
      sessionStorage.removeItem('lavyn_generation_job');
      sessionStorage.removeItem('scan_input_type');
      sessionStorage.removeItem('scan_help_mode');
      if (image) sessionStorage.setItem('scan_input_image', image);
      else sessionStorage.removeItem('scan_input_image');
      sessionStorage.setItem('scan_context', JSON.stringify({
        subject: 'Auto-detect',
        grade: '10',
        topic: cleanPrompt || 'Explain the attached question',
        fromTypedPrompt: !image,
        homeLearningMode: 'effective',
        fromHomeComposer: true,
      }));
      sessionStorage.setItem('scan_instruction', cleanPrompt || 'Explain the attached question clearly.');
      sessionStorage.setItem('scan_output_mode', 'video');
    } catch {
      setFileError('Your study request could not be prepared. Check available storage and try again.');
      return;
    }
    navigate('/scan/analyzing');
  };

  const handleComposerImage = (event: ChangeEvent<HTMLInputElement>, capture = false) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFileError('Choose an image file to attach it to your question.');
      return;
    }
    setFileError('');
    const readId = ++attachmentReadRef.current;
    const reader = new FileReader();
    reader.onerror = () => setFileError('That image could not be read. Please choose it again.');
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        setFileError('That image could not be read. Please choose it again.');
        return;
      }
      const image = new Image();
      image.onerror = () => {
        if (readId === attachmentReadRef.current) setFileError('This image could not be opened. Try a JPEG or PNG photo.');
      };
      image.onload = () => {
        if (readId !== attachmentReadRef.current) return;
        try {
          const scale = Math.min(1, 2048 / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
          const context = canvas.getContext('2d');
          if (!context) throw Error('Image processing unavailable');
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          setImageData(canvas.toDataURL('image/jpeg', .9));
        } catch {
          setFileError('This photo could not be prepared. Please choose it again.');
        }
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
    if (capture) setUploadStatus('');
  };

  return (
    <div className="lavyn-home" data-active={location.pathname === '/'}>
      <div className="lavyn-home-inner">
        <header className="lavyn-home-header">
          <button type="button" className="lavyn-wordmark lavyn-tap" onClick={() => setShowStudyMenu(true)} aria-label="LAVYN — browse your subjects" title="Browse your subjects">
            <h1>LAVYN</h1>
            <p>Your personal study tutor</p>
          </button>
          <button type="button" className="lavyn-profile lavyn-tap" aria-label="Open profile" onClick={() => navigate('/profile')}>
            <UserRound aria-hidden="true" />
          </button>
        </header>

        <section className="lavyn-snap lavyn-glass" aria-labelledby="snap-title">
          <button type="button" className="lavyn-upload lavyn-tap" onClick={() => setShowUploadSheet(true)} aria-label="Upload a homework image or PDF" title="Upload a homework image or PDF">
            <Upload aria-hidden="true" />
          </button>
          <button type="button" className="lavyn-camera lavyn-tap" onClick={handleScanClick} aria-label="Take a photo of your homework">
            <Camera aria-hidden="true" />
          </button>
          <button type="button" className="lavyn-snap-title lavyn-tap" id="snap-title" onClick={handleScanClick}>Snap &amp; Solve</button>
          <p>A photo. A clear explanation. One step at a time.</p>
        </section>

        {fileError && (
          <div className="lavyn-file-error" role="alert">
            {fileError}
            <button type="button" onClick={() => setShowUploadSheet(true)}>Try again</button>
          </div>
        )}

        <section className="lavyn-tools" aria-label="Study tools">
          {tools.map(({ id, title, description, icon: Icon }) => (
            <button type="button" key={id} className="lavyn-tool lavyn-glass lavyn-tap" onClick={() => setActiveTool(id as StudyTool)} aria-label={`${title}: ${description}. Get started.`}>
              <span className="lavyn-tool-icon"><Icon aria-hidden="true" /></span>
              <h2>{title}</h2>
              <p>{description}</p>
            </button>
          ))}
        </section>

        <div aria-label="LAVYN information" className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1 text-xs text-[var(--home-muted)]">
          <Link to="/about" className="inline-flex min-h-11 items-center underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">About LAVYN</Link>
          <Link to="/contact" className="inline-flex min-h-11 items-center underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">Help &amp; Support</Link>
        </div>
      </div>

      <HomeComposer
        prompt={composerPrompt}
        onPromptChange={setComposerPrompt}
        imagePreview={imageData}
        onRemoveImage={() => { attachmentReadRef.current++; setImageData(null); }}
        onOpenCamera={() => composerCameraRef.current?.click()}
        onChooseImage={() => composerImageRef.current?.click()}
        onGenerate={() => startGeneration(composerPrompt, imageData)}
        isReady={isRehydrated}
        errorMessage={fileError}
      />

      <AccountRequiredGate
        open={accountGateOpen}
        isDark={resolvedTheme === 'dark'}
        onClose={() => setAccountGateOpen(false)}
      />

      {activeTool && location.pathname === '/' && <ToolStart key={activeTool} tool={activeTool} onClose={closeTool} />}

      <input ref={scanInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => handleFileSelected(event)} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => handleFileSelected(event)} />
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => handleFileSelected(event)} />
      <input ref={composerCameraRef} aria-label="Take a composer photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => handleComposerImage(event, true)} />
      <input ref={composerImageRef} aria-label="Choose a composer photo" type="file" accept="image/*" className="hidden" onChange={(event) => handleComposerImage(event)} />
      <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(event) => handleFileSelected(event, 'Processing PDF')} />
      <UploadSheet isOpen={showUploadSheet} onClose={() => setShowUploadSheet(false)} onSelect={handleUploadSelect} theme={resolvedTheme} isDesktop={isDesktop} />
      <LoadingOverlay isVisible={!!uploadStatus} theme={resolvedTheme} status={uploadStatus} />
      <StudyMenu isOpen={showStudyMenu} onClose={() => setShowStudyMenu(false)} />
      <DemoGate feature="default" open={showReturningGate} onClose={() => setShowReturningGate(false)} />
    </div>
  );
}